/**
 * Ship Rendering System
 *
 * Handles ship sprite creation, updates, HP bars, and tooltips.
 * Extracted from OceanScene for modularity.
 */

import Phaser from "phaser";
import {
  TILE_SIZE,
  COLORS,
  NPC_BEHAVIOR_COLORS,
  NPC_FACTION_COLORS,
  ShipData,
  ShipType,
  SHIP_SIZE_MULTIPLIERS,
} from "../types";
import { HITBOX_MULTIPLIER } from "../constants/hitbox";
import { formatDisplayName } from "../../utils/formatDisplayName";
import { drawAggroRange } from "../utils/RadiusRenderer";

const SERVER_TICK_RATE = 10; // Must match server/src/movement/GridMovement.ts

// Ship sprite interface
export interface ShipSprite extends Phaser.GameObjects.Container {
  shipId?: string; // Ship ID from server
  shipData?: any; // Ship data from server
  hpBar?: Phaser.GameObjects.Graphics; // HP bar UI (graphics object for drawing)
  lastDirection?: number;
  aggroGlow?: Phaser.GameObjects.Graphics;
  nameContainer?: Phaser.GameObjects.Container; // Container for nameplate (text + background)
  nameLabel?: Phaser.GameObjects.Text;
  flagMarker?: Phaser.GameObjects.Graphics;
  movementTween?: Phaser.Tweens.Tween;
  bobbingTween?: Phaser.Tweens.Tween;
  lastUpdateTime?: number;
  lastPosition?: { x: number; y: number };
  lastScaleX?: number;
  lastScaleY?: number;
  hull?: any; // Shape (Circle/Rectangle) or Container for ship hull
  tweenStartPos?: { x: number; y: number }; // Track where tween started
  tweenStartTime?: number; // Track when tween started
}

export interface ShipRendererCallbacks {
  onShipClick: (shipId: string) => void;
  onShipHover: (ship: ShipSprite) => void;
  onShipHoverEnd: () => void;
  getLocalPlayerId: () => string;
  getSelectedTarget: () => string | null;
  getLocalShip: () => ShipSprite | null;
}

export class ShipRenderer {
  private scene: Phaser.Scene;
  private ships: Map<string, ShipSprite> = new Map();
  private callbacks: ShipRendererCallbacks;
  private localShip: ShipSprite | null = null;
  private shipTooltip: Phaser.GameObjects.Container | null = null;
  private visibilityLimitTiles: number | null = null;
  private speedDebugText: Phaser.GameObjects.Text | null = null;
  // OPTIMIZATION: Cache visibility state to avoid per-frame checks
  private shipVisibilityCache: Map<
    string,
    { x: number; y: number; visible: boolean }
  > = new Map();

  private getShipDepth(y: number): number {
    // Ships should render below fog/weather overlay (depth 120)
    // Use normalized Y for depth (0-100 range to stay under fog layer)
    return 50 + (Math.floor(y) % 50);
  }

  private findShipPart(
    container: Phaser.GameObjects.Container,
    part: string,
  ): Phaser.GameObjects.GameObject | null {
    for (const child of container.list) {
      const anyChild = child as any;
      if (anyChild?.getData && anyChild.getData("shipPart") === part) {
        return child;
      }
    }
    return null;
  }

  constructor(scene: Phaser.Scene, callbacks: ShipRendererCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  getShips(): Map<string, ShipSprite> {
    return this.ships;
  }

  getLocalShip(): ShipSprite | null {
    return this.localShip;
  }

  setLocalShip(ship: ShipSprite | null): void {
    this.localShip = ship;
    this.ensureSpeedDebugText();
    this.applyVisibilityLimits();
  }

  private ensureSpeedDebugText(): void {
    if (this.speedDebugText) {
      return;
    }
    this.speedDebugText = this.scene.add.text(0, 0, "", {
      fontFamily:
        "Sometype Mono, ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: "10px",
      color: "#f0f6fc",
      backgroundColor: "rgba(13, 17, 23, 0.85)",
      padding: { left: 6, right: 6, top: 4, bottom: 4 },
    });
    this.speedDebugText.setDepth(200);
  }

  setVisibilityLimit(limitTiles: number | null): void {
    this.visibilityLimitTiles = limitTiles;
    this.applyVisibilityLimits();
  }

  /**
   * Create a ship sprite
   */
  createShipSprite(data: ShipData): ShipSprite {
    // Guard against duplicate creation
    const existing = this.ships.get(data.id);
    if (existing) {
      console.warn(
        `⚠️ Ship ${data.id} already exists, skipping duplicate creation`,
      );
      return existing;
    }

    // Debug: Ship sprite creation (commented out to reduce console spam)
    // console.log(`🚢 Creating ship sprite: ${data.id} at (${data.x}, ${data.y}), total ships: ${this.ships.size}`);
    const container = this.scene.add.container(data.x, data.y) as ShipSprite;
    container.shipId = data.id;
    container.shipData = data;
    // Set depth so ships and their UI render above island tiles (depth 3)
    container.setDepth(this.getShipDepth(data.y));

    const localPlayerId = this.callbacks.getLocalPlayerId();
    const isLocal = data.ownerId === localPlayerId;
    const isNPC = data.ownerType === "npc";

    // Determine ship color
    const color = this.getShipColor(data, isLocal, isNPC);

    const finalSize = this.getFinalShipSize(data, isNPC);

    let hull: Phaser.GameObjects.Shape | Phaser.GameObjects.Container;

    // Check if in sanctum (applies to both NPCs and players)
    const oceanScene = this.scene as any;
    const isInSanctum = oceanScene.isInSanctumInstance || false;

    if (isInSanctum) {
      // In sanctums: use simple circles for NPCs, squares for players
      if (isNPC) {
        hull = this.scene.add.circle(
          0,
          0,
          finalSize / 2,
          color,
        ) as Phaser.GameObjects.Arc;
      } else {
        hull = this.scene.add.rectangle(0, 0, finalSize, finalSize, color);
      }
      container.add(hull);
    } else {
      // In ocean: use ship visuals for both NPCs and players
      const visualType = data.shipType || "sloop";
      const faction = data.ownerType === "npc" ? data.faction : undefined;
      const shipContainer = this.createShipVisual(
        visualType,
        finalSize,
        color,
        data.ownerType === "npc" ? data.id : undefined,
        faction,
      );
      hull = shipContainer;
      container.add(hull);
    }
    container.hull = hull as Phaser.GameObjects.Shape;

    // Initialize raft direction for visual orientation
    if (data.shipType === "raft") {
      container.lastDirection = 0; // Start facing right (0°)
    }

    // Aggro indicator for NPC ships - grid-based aggro range visualization
    if (isNPC) {
      const aggroGlow = this.scene.add.graphics();
      aggroGlow.setVisible(false);
      container.addAt(aggroGlow, 0);
      container.aggroGlow = aggroGlow;
    }

    // Name label below player ships (fleet tag + optional title + name)
    if (!isNPC) {
      const labelY = finalSize / 2 + 6;
      const displayName = this.buildPlayerDisplayName(data).toUpperCase();
      const labelColor = this.getPlayerNameColor(data);

      // Create a container for the nameplate with background
      const nameContainer = this.scene.add.container(0, labelY);

      // Create text object - 20% smaller
      const nameText = this.scene.add.text(0, 0, displayName, {
        fontFamily: '"Sometype Mono", monospace',
        fontSize: "7px", // Was 12px, reduced 20%
        color: labelColor,
        align: "center",
      });
      nameText.setOrigin(0.5, 0.5);

      // Create background rectangle with padding - slightly less padding
      const padding = 3; // Was 4
      const bg = this.scene.add.rectangle(
        0,
        0,
        nameText.width + padding + 2,
        nameText.height + padding,
        0x000000, // Black background
        1, // 85% opacity for slight transparency
      );
      bg.setOrigin(0.5, 0.5);
      bg.setDepth(-1); // Behind text

      nameContainer.add(bg);
      nameContainer.add(nameText);

      nameContainer.setVisible(!isLocal || this.shouldShowMyNameLabel());
      nameContainer.setDepth(100);
      container.add(nameContainer);
      container.nameContainer = nameContainer; // Keep reference to container for visibility updates
      container.nameLabel = nameText; // Keep reference to text for setText updates
    }

    if (!isNPC) {
      this.renderShipFlag(container, data, finalSize);
    }

    // Create HP bar (hidden by default, only shown during combat/hover)
    const hpBar = this.scene.add.graphics();
    hpBar.setVisible(false);
    container.add(hpBar);
    container.hpBar = hpBar;

    // Make interactive - enlarged hitbox for easier clicking
    const hitboxSize = finalSize * HITBOX_MULTIPLIER;
    const hitArea = isNPC
      ? new Phaser.Geom.Circle(0, 0, hitboxSize / 2)
      : new Phaser.Geom.Rectangle(
          -hitboxSize / 2,
          -hitboxSize / 2,
          hitboxSize,
          hitboxSize,
        );
    const hitCallback = isNPC
      ? Phaser.Geom.Circle.Contains
      : Phaser.Geom.Rectangle.Contains;
    hull.setInteractive(hitArea, hitCallback);

    hull.on(
      "pointerdown",
      (
        pointer: Phaser.Input.Pointer,
        _localX: number,
        _localY: number,
        event: Phaser.Types.Input.EventData,
      ) => {
        // Only handle left-clicks for combat engagement
        // Right-clicks should propagate to InputHandler for context menu
        // Don't stop propagation - let resource nodes and other interactive objects handle clicks
        if (pointer.leftButtonDown()) {
          if (data.ownerId !== localPlayerId) {
            this.callbacks.onShipClick(data.id);
          }
        }
      },
    );

    hull.on("pointerover", () => {
      this.callbacks.onShipHover(container);
    });

    hull.on("pointerout", () => {
      this.callbacks.onShipHoverEnd();
    });

    this.ships.set(data.id, container);

    if (isLocal) {
      this.localShip = container;
    }

    // Add gentle bobbing motion animation
    this.addBobbingAnimation(container);

    return container;
  }

  /**
   * Update ships from server data
   */
  updateShips(shipsData: ShipData[], onLocalShipMoved?: () => void): void {
    // Deduplicate incoming data: keep only latest update for each ship ID
    const latestByShipId = new Map<string, ShipData>();
    for (const ship of shipsData) {
      latestByShipId.set(ship.id, ship);
    }
    const deduplicatedData = Array.from(latestByShipId.values());

    const existingIds = new Set(deduplicatedData.map((s) => s.id));
    const localPlayerId = this.callbacks.getLocalPlayerId();
    const now = Date.now();

    deduplicatedData.forEach((data) => {
      const ship = this.ships.get(data.id);

      if (ship) {
        const previousFlagPattern = ship.shipData?.flagPattern;
        const previousFlagPrimary = ship.shipData?.flagPrimaryColor;
        const previousFlagSecondary = ship.shipData?.flagSecondaryColor;
        const previousFlagEmblem = ship.shipData?.flagEmblem;
        const previousFlagEmblemColor = ship.shipData?.flagEmblemColor;
        const oldX = ship.x;
        const oldY = ship.y;

        // Check if ship type changed (requires visual recreation)
        const shipTypeChanged = ship.shipData.shipType !== data.shipType;

        // Merge ship data
        const mergedShipData: ShipData = {
          ...ship.shipData,
          ...data,
          shipColor: data.shipColor ?? ship.shipData.shipColor,
        };
        ship.shipData = mergedShipData;

        // If ship type changed, recreate the hull visual
        if (shipTypeChanged) {
          // Stop all tweens on the old hull
          if (ship.movementTween) {
            ship.movementTween.stop();
            ship.movementTween = undefined;
          }
          if (ship.bobbingTween) {
            ship.bobbingTween.stop();
            ship.bobbingTween = undefined;
          }

          // Remove old hull from container
          if (ship.hull) {
            ship.hull.destroy();
          }

          // Create new hull with updated ship type
          const oceanScene = this.scene as any;
          const isInSanctum = oceanScene.isInSanctumInstance || false;
          const isNPC = data.ownerType === "npc";
          const color = this.getShipColor(
            data,
            data.ownerId === this.callbacks.getLocalPlayerId(),
            isNPC,
          );
          const shipType = data.shipType as ShipType | undefined;
          const sizeMultiplier = shipType
            ? SHIP_SIZE_MULTIPLIERS[shipType]
            : 0.6;
          const shipSize = TILE_SIZE * sizeMultiplier;
          const npcSizeReduction = isNPC ? 0.85 : 1.0;
          const finalSize = shipSize * npcSizeReduction * 3;

          let hull: Phaser.GameObjects.Shape | Phaser.GameObjects.Container;

          if (isInSanctum) {
            if (isNPC) {
              hull = this.scene.add.circle(
                0,
                0,
                finalSize / 2,
                color,
              ) as Phaser.GameObjects.Arc;
            } else {
              hull = this.scene.add.rectangle(
                0,
                0,
                finalSize,
                finalSize,
                color,
              );
            }
          } else {
            const visualType = data.shipType || "sloop";
            const faction = data.ownerType === "npc" ? data.faction : undefined;
            const shipContainer = this.createShipVisual(
              visualType,
              finalSize,
              color,
              undefined,
              faction,
            );
            hull = shipContainer;
          }

          ship.add(hull);
          ship.hull = hull as Phaser.GameObjects.Shape;

          // Re-setup interactivity on new hull
          const hitboxSize = finalSize * HITBOX_MULTIPLIER;
          const hitArea = isNPC
            ? new Phaser.Geom.Circle(0, 0, hitboxSize / 2)
            : new Phaser.Geom.Rectangle(
                -hitboxSize / 2,
                -hitboxSize / 2,
                hitboxSize,
                hitboxSize,
              );
          const hitCallback = isNPC
            ? Phaser.Geom.Circle.Contains
            : Phaser.Geom.Rectangle.Contains;
          hull.setInteractive(hitArea, hitCallback);

          hull.on(
            "pointerdown",
            (
              pointer: Phaser.Input.Pointer,
              _localX: number,
              _localY: number,
              event: Phaser.Types.Input.EventData,
            ) => {
              if (pointer.leftButtonDown()) {
                event.stopPropagation();
                if (data.ownerId !== this.callbacks.getLocalPlayerId()) {
                  this.callbacks.onShipClick(data.id);
                }
              }
            },
          );

          hull.on("pointerover", () => {
            this.callbacks.onShipHover(ship);
          });

          hull.on("pointerout", () => {
            this.callbacks.onShipHoverEnd();
          });
        }

        // Smooth continuous interpolation: calculate tween duration based on distance and speed
        // This eliminates tile-boundary pauses by smoothly continuing motion
        if (oldX !== data.x || oldY !== data.y) {
          ship.setDepth(this.getShipDepth(data.y));
          // Calculate distance moved
          const dx = data.x - oldX;
          const dy = data.y - oldY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance === 0) {
            return;
          }

          // Apply horizontal flipping for all ships (left/right only)
          if (dx !== 0) {
            const targetScaleX = dx < 0 ? -1 : 1;
            if (ship.lastScaleX !== targetScaleX) {
              ship.setScale(targetScaleX, 1);
              ship.lastScaleX = targetScaleX;

              // Also flip the name label to match ship direction (keep size stable)
              if (ship.nameLabel) {
                ship.nameLabel.setScale(targetScaleX, 0.8);
              }
            }
          }

          // During sprint, rapid updates can cause visual jitter/duplicates
          // Skip updates that are too close to current position to let tween complete smoothly
          if (ship.movementTween && ship.movementTween.isPlaying()) {
            // If new target is very close to ship's current position, skip the update
            // The tween is already animating, let it finish naturally
            const distToNewTarget = Math.hypot(
              ship.x - data.x,
              ship.y - data.y,
            );

            // If we're already very close (less than 0.4 tiles) to the new target, skip
            // This happens when server sends rapid updates during sprint
            if (distToNewTarget < TILE_SIZE * 0.4) {
              // Very close - current tween will get us there soon enough
              return;
            }

            // Stop old tween if we need significant course correction
            ship.movementTween.stop();
            ship.movementTween = undefined;
          }

          // Use speed from server movement state to calculate tween duration
          // Server sends speed in tiles/sec, convert to pixels/sec
          // Sprint applies 1.5x multiplier on server
          const serverSpeed = data.movement?.speed ?? 0.4; // tiles/tick from server
          const baseTilesPerSec = serverSpeed * SERVER_TICK_RATE; // convert to tiles/sec
          const isSprinting = data.isSprinting || false;
          const finalTilesPerSec = isSprinting
            ? baseTilesPerSec * 1.5
            : baseTilesPerSec;
          const speedPixelsPerSec = finalTilesPerSec * TILE_SIZE; // pixels/sec
          const distanceDurationMs = (distance / speedPixelsPerSec) * 1000;
          const duration = Math.max(100, distanceDurationMs);

          // Create wake trail effect
          this.createWakeTrail(ship, oldX, oldY, data.x, data.y);

          // Create smooth interpolation tween that matches server movement speed
          ship.movementTween = this.scene.tweens.add({
            targets: ship,
            x: data.x,
            y: data.y,
            duration: duration,
            ease: "Linear",
            onComplete: () => {
              // Stop bobbing when movement completes
              if (ship.bobbingTween) {
                ship.bobbingTween.stop();
                ship.bobbingTween = undefined;
              }
              // Reset hull to original Y position
              if (ship.hull instanceof Phaser.GameObjects.Container) {
                ship.hull.y = 0;
              } else {
                (ship.hull as any).y = 0;
              }
            },
          });

          // Add subtle bobbing animation while moving (2px vertical oscillation)
          // Only start bobbing if distance > 0 (ship is actually moving)
          if (distance > 0) {
            if (ship.bobbingTween) {
              ship.bobbingTween.stop();
              ship.bobbingTween = undefined;
            }
            // Use absolute positioning to avoid accumulation
            // Reset to 0 first, then bob between 0 and 2
            if (ship.hull instanceof Phaser.GameObjects.Container) {
              ship.hull.y = 0;
            } else {
              (ship.hull as any).y = 0;
            }
            ship.bobbingTween = this.scene.tweens.add({
              targets: ship.hull,
              y: 2,
              duration: 600,
              ease: "Sine.easeInOut",
              yoyo: true,
              repeat: -1,
            });
          }

          ship.lastUpdateTime = now;
          ship.lastPosition = { x: oldX, y: oldY };

          // Update speed debug for moving local player
          if (data.ownerId === localPlayerId && this.speedDebugText) {
            const speedText = `SPD ${finalTilesPerSec.toFixed(2)} t/s | ${speedPixelsPerSec.toFixed(0)} px/s (tick ${serverSpeed.toFixed(2)})`;
            this.speedDebugText.setText(speedText);
            this.speedDebugText.setPosition(data.x + 18, data.y - 40);
          }
        } else {
          // Not moving, but still update speed debug for local player
          if (data.ownerId === localPlayerId && this.speedDebugText) {
            const serverSpeed = data.movement?.speed ?? 0.4;
            const baseTilesPerSec = serverSpeed * SERVER_TICK_RATE;
            const speedPixelsPerSec = baseTilesPerSec * TILE_SIZE;
            const speedText = `SPD ${baseTilesPerSec.toFixed(2)} t/s | ${speedPixelsPerSec.toFixed(0)} px/s (tick ${serverSpeed.toFixed(2)}) [IDLE]`;
            this.speedDebugText.setText(speedText);
            this.speedDebugText.setPosition(data.x + 18, data.y - 40);
          }
        }

        // Update localShip reference
        if (data.ownerId === localPlayerId) {
          this.localShip = ship;
          if ((oldX !== data.x || oldY !== data.y) && onLocalShipMoved) {
            onLocalShipMoved();
          }
        }

        this.updateShipAggroVisual(ship);
        this.updateShipColor(ship, mergedShipData);
        this.updateNameLabel(ship, mergedShipData);
        if (mergedShipData.ownerType === "player") {
          const flagChanged =
            previousFlagPattern !== mergedShipData.flagPattern ||
            previousFlagPrimary !== mergedShipData.flagPrimaryColor ||
            previousFlagSecondary !== mergedShipData.flagSecondaryColor ||
            previousFlagEmblem !== mergedShipData.flagEmblem ||
            previousFlagEmblemColor !== mergedShipData.flagEmblemColor ||
            !ship.flagMarker;
          if (flagChanged) {
            const shipSize = this.getFinalShipSize(mergedShipData, false);
            this.renderShipFlag(ship, mergedShipData, shipSize);
          }
        }

        // OPTIMIZATION: Invalidate visibility cache when position changes
        this.shipVisibilityCache.delete(data.id);
      } else {
        if (!this.ships.has(data.id)) {
          this.createShipSprite(data);
        }
      }
    });

    // Remove ships that no longer exist
    this.ships.forEach((ship, id) => {
      if (!existingIds.has(id)) {
        this.removeShip(id);
      }
    });

    // Apply visibility limits only if they exist
    if (this.visibilityLimitTiles !== null) {
      this.applyVisibilityLimits();
    }
  }

  /**
   * Remove a ship with sink animation
   */
  removeShip(shipId: string, onRemoved?: () => void): void {
    const ship = this.ships.get(shipId);
    if (ship) {
      const selectedTarget = this.callbacks.getSelectedTarget();
      if (selectedTarget === shipId && onRemoved) {
        onRemoved();
      }

      // Stop any active movement tween
      if (ship.movementTween) {
        ship.movementTween.stop();
      }

      // Stop bobbing animation
      if (ship.bobbingTween) {
        ship.bobbingTween.stop();
      }

      this.scene.tweens.add({
        targets: ship,
        alpha: 0,
        duration: 1000,
        onComplete: () => {
          ship.destroy();
        },
      });
      this.ships.delete(shipId);

      if (this.localShip?.shipId === shipId) {
        this.localShip = null;
      }
    }
    this.applyVisibilityLimits();
  }

  /**
   * Update HP bar for a ship
   * Only show HP bar during combat or when explicitly requested
   */
  updateShipHPBar(ship: ShipSprite, shouldShow: boolean = false): void {
    const hpBar = ship.hpBar;
    if (!hpBar) return; // HP bar not initialized

    // Only render if should show
    if (!shouldShow) {
      hpBar.clear();
      hpBar.setVisible(false);
      return;
    }

    hpBar.setVisible(true);
    hpBar.clear();
    // Ensure health bar renders above islands
    hpBar.setDepth(101);

    const data = ship.shipData;
    const hpPercent = data.hullHp / data.maxHullHp;

    // Scale HP bar with ship size
    const shipType = data.shipType as ShipType | undefined;
    const sizeMultiplier = shipType ? SHIP_SIZE_MULTIPLIERS[shipType] : 0.6;
    const isNPC = data.ownerType === "npc";
    const npcReduction = isNPC ? 0.85 : 1.0;
    const shipSize = TILE_SIZE * sizeMultiplier * npcReduction;

    const barWidth = shipSize;
    const barHeight = 3;
    const yOffset = shipSize / 2 + 2;

    // Background
    hpBar.fillStyle(0x000000, 0.8);
    hpBar.fillRect(-barWidth / 2, yOffset, barWidth, barHeight);

    // HP fill
    const hpColor =
      hpPercent > 0.5 ? 0x2ecc71 : hpPercent > 0.25 ? 0xf39c12 : 0xe74c3c;
    hpBar.fillStyle(hpColor, 1);
    hpBar.fillRect(-barWidth / 2, yOffset, barWidth * hpPercent, barHeight);

    // Border
    hpBar.lineStyle(1, 0xffffff, 0.5);
    hpBar.strokeRect(-barWidth / 2, yOffset, barWidth, barHeight);
  }

  /**
   * Update aggro visual for NPC ships - uses grid-based aggro range visualization
   */
  private updateShipAggroVisual(ship: ShipSprite): void {
    const data = ship.shipData;
    if (data.ownerType !== "npc" || !ship.aggroGlow) return;

    const localShip = this.callbacks.getLocalShip();
    const localShipId = localShip?.shipId || null;
    const isTargetingLocal =
      !!localShipId && data.currentTarget === localShipId;
    const shouldShow = !!data.isAggroed && isTargetingLocal;

    ship.aggroGlow.clear();
    ship.aggroGlow.setVisible(shouldShow);

    if (shouldShow && data.aggroRange) {
      const rangeTiles = Math.ceil(data.aggroRange / TILE_SIZE);
      // Draw at 0,0 since container is positioned at ship location
      drawAggroRange(ship.aggroGlow, 0, 0, rangeTiles, true);
    }
  }

  /**
   * Build display name for player ships (optional fleet tag + optional title + name)
   */
  private buildPlayerDisplayName(data: ShipData): string {
    return formatDisplayName({
      baseName: data.ownerName,
      fleetTag: data.fleetTag,
      titleName: data.titleName,
    });
  }

  /**
   * Update name label for player ships
   */
  private updateNameLabel(ship: ShipSprite, data: ShipData): void {
    if (!ship.nameLabel || !ship.nameContainer) return;

    const displayName = this.buildPlayerDisplayName(data).toUpperCase();
    ship.nameLabel.setText(displayName);

    const isLocal: boolean = data.ownerId === this.callbacks.getLocalPlayerId();
    const shouldShow: boolean = isLocal
      ? this.shouldShowMyNameLabel()
      : this.shouldShowOtherPlayerNames();

    // Update both the container (background + text) and text visibility
    ship.nameContainer.setVisible(shouldShow);
    ship.nameLabel.setVisible(shouldShow);

    ship.nameLabel.setColor(this.getPlayerNameColor(data));
  }

  private getFinalShipSize(data: ShipData, isNPC: boolean): number {
    const shipType = data.shipType as ShipType | undefined;
    const sizeMultiplier = shipType ? SHIP_SIZE_MULTIPLIERS[shipType] : 0.6;
    const shipSize = TILE_SIZE * sizeMultiplier;
    const npcSizeReduction = isNPC ? 0.85 : 1.0;
    return shipSize * npcSizeReduction * 3;
  }

  private renderShipFlag(
    ship: ShipSprite,
    data: ShipData,
    finalSize: number,
  ): void {
    const isLocal: boolean = data.ownerId === this.callbacks.getLocalPlayerId();

    // For local player's own flag, check showMyFlag
    // For other players' flags, check showOtherPlayerFlags
    const shouldShowFlag: boolean = isLocal
      ? this.shouldShowMyFlag()
      : this.shouldShowOtherPlayerFlags();

    if (!shouldShowFlag || data.ownerType !== "player") {
      if (ship.flagMarker) {
        ship.flagMarker.destroy();
        ship.flagMarker = undefined;
      }
      return;
    }

    const labelY = finalSize / 2 + 6;
    const flagWidth = 18;
    const flagHeight = 12;
    const flagY = labelY + 16;

    const primaryColor: number = this.hexToNumber(
      data.flagPrimaryColor,
      0x1a1a2e,
    );
    const secondaryColor: number = this.hexToNumber(
      data.flagSecondaryColor,
      0x4a5568,
    );
    const emblemColor: number = this.hexToNumber(
      data.flagEmblemColor,
      0xffffff,
    );

    if (ship.flagMarker) {
      ship.flagMarker.destroy();
    }

    const flag = this.scene.add.graphics();
    flag.clear();
    flag.setDepth(100);
    const patternId: string = data.flagPattern || "solid";
    this.drawFlagPattern(
      flag,
      patternId,
      -flagWidth / 2,
      flagY,
      flagWidth,
      flagHeight,
      primaryColor,
      secondaryColor,
    );

    const emblemId: string = data.flagEmblem || "none";
    const emblemSize: number = 8;
    const emblemX: number = -flagWidth / 2 + flagWidth / 2 - emblemSize / 2;
    const emblemY: number = flagY + flagHeight / 2 - emblemSize / 2;
    this.drawFlagEmblem(
      flag,
      emblemId,
      emblemX,
      emblemY,
      emblemSize,
      emblemColor,
    );

    flag.lineStyle(1, secondaryColor, 1);
    flag.strokeRect(-flagWidth / 2, flagY, flagWidth, flagHeight);

    ship.add(flag);
    ship.flagMarker = flag;
  }

  private getPlayerNameColor(data: ShipData): string {
    if (data.ownerType !== "player") return "#ffffff";

    if (data.accountStatus === "admin") return "#f1c40f";
    if (data.accountStatus === "moderator") return "#3b82f6";

    return data.titleColor || "#ffffff";
  }

  private shouldShowMyNameLabel(): boolean {
    try {
      const saved: string | null = localStorage.getItem("showMyNameLabel");
      return saved === null ? true : saved === "true";
    } catch {
      return true;
    }
  }

  private shouldShowMyFlag(): boolean {
    try {
      const saved: string | null = localStorage.getItem("showMyFlag");
      return saved === null ? true : saved === "true";
    } catch {
      return true;
    }
  }

  private shouldShowOtherPlayerNames(): boolean {
    try {
      const saved: string | null = localStorage.getItem("showOtherPlayerNames");
      return saved === null ? true : saved === "true";
    } catch {
      return true;
    }
  }

  private shouldShowOtherPlayerFlags(): boolean {
    try {
      const saved: string | null = localStorage.getItem("showOtherPlayerFlags");
      return saved === null ? true : saved === "true";
    } catch {
      return true;
    }
  }

  /**
   * Public method to refresh player identity visibility when settings change
   * Call this when the user toggles visibility settings
   */
  public refreshAllVisibility(): void {
    const localPlayerId = this.callbacks.getLocalPlayerId();

    this.ships.forEach((ship) => {
      if (ship.shipData) {
        const isLocal = ship.shipData.ownerId === localPlayerId;

        // Refresh nameplate visibility
        if (ship.nameContainer) {
          const shouldShowName = isLocal
            ? this.shouldShowMyNameLabel()
            : this.shouldShowOtherPlayerNames();
          ship.nameContainer.setVisible(shouldShowName);
        }

        // Refresh flag visibility
        if (isLocal) {
          // For local player, check if they want to see their own flag
          const shouldShowFlag = this.shouldShowMyFlag();
          if (
            shouldShowFlag &&
            !ship.flagMarker &&
            ship.shipData.ownerType === "player"
          ) {
            // Flag should exist but doesn't - recreate it
            const shipSize = this.getFinalShipSize(ship.shipData, false);
            this.renderShipFlag(ship, ship.shipData, shipSize);
          } else if (!shouldShowFlag && ship.flagMarker) {
            // Flag should not exist but does - destroy it
            ship.flagMarker.destroy();
            ship.flagMarker = undefined;
          }
        } else {
          // For other players, check if we want to see other players' flags
          const shouldShowFlag = this.shouldShowOtherPlayerFlags();
          if (
            shouldShowFlag &&
            !ship.flagMarker &&
            ship.shipData.ownerType === "player"
          ) {
            // Flag should exist but doesn't - recreate it
            const shipSize = this.getFinalShipSize(ship.shipData, false);
            this.renderShipFlag(ship, ship.shipData, shipSize);
          } else if (!shouldShowFlag && ship.flagMarker) {
            // Flag should not exist but does - destroy it
            ship.flagMarker.destroy();
            ship.flagMarker = undefined;
          }
        }
      }
    });
  }

  /**
   * Calculate direction from movement vector (dx, dy)
   * Returns the primary direction as rotation angle and flip state
   */
  private calculateDirection(
    dx: number,
    dy: number,
  ): { angle: number; scaleX: number; scaleY: number } {
    // Determine primary direction based on absolute magnitudes
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    const isHorizontalMove = absDx > absDy;

    // Horizontal movement (left/right)
    if (isHorizontalMove) {
      // Facing right (0°) or left (180°)
      return {
        angle: 0,
        scaleX: dx < 0 ? -1 : 1,
        scaleY: 1,
      };
    }

    // Vertical movement (up/down)
    const isMostlyUp = dy < 0;
    return {
      angle: isMostlyUp ? 90 : 270, // 90° for up, 270° for down
      scaleX: 1,
      scaleY: 1,
    };
  }

  /**
   * Create ship visual based on type (sloop, brigantine, frigate, etc)
   */
  private createShipVisual(
    shipType: string,
    finalSize: number,
    color: number,
    shipId?: string,
    faction?: string,
  ): Phaser.GameObjects.Container {
    const shipContainer = this.scene.add.container(0, 0);
    const w = finalSize / 2;
    const h = finalSize / 2;

    switch (shipType) {
      case "raft":
        this.createRaftVisual(shipContainer, w, h, color, shipId, faction);
        break;
      case "rowboat":
      case "sloop":
        this.createSloopVisual(shipContainer, w, h, color, shipId, faction);
        break;
      case "caravel":
        this.createBrigantineVisual(
          shipContainer,
          w,
          h,
          color,
          shipId,
          faction,
        );
        break;
      case "brigantine":
        this.createBrigantineVisual(
          shipContainer,
          w,
          h,
          color,
          shipId,
          faction,
        );
        break;
      case "frigate":
        this.createFrigateVisual(shipContainer, w, h, color, shipId, faction);
        break;
      case "galleon":
      case "man_o_war":
        this.createGalleonVisual(shipContainer, w, h, color, shipId, faction);
        break;
      default:
        this.createSloopVisual(shipContainer, w, h, color, shipId, faction);
    }

    return shipContainer;
  }

  /**
   * Add a triangle sail at the specified position with rotation
   * Triangle sails provide visual variety to square/rectangular sails
   */
  private addTriangleSail(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    width: number,
    height: number,
    color: number,
    rotation: number,
  ): void {
    // Create right-angle triangle sail using a graphics object
    // Right angle at bottom-left, with hypotenuse as the sail face
    const graphics = this.scene.add.graphics();
    graphics.fillStyle(color, 0.95);

    // Draw right-angle triangle:
    // Bottom-left corner (right angle): (0, height)
    // Top-left corner: (0, 0)
    // Bottom-right corner: (width, height)
    graphics.beginPath();
    graphics.moveTo(0, height); // Bottom-left (right angle)
    graphics.lineTo(0, 0); // Top-left
    graphics.lineTo(width, height); // Bottom-right
    graphics.closePath();
    graphics.fillPath();

    // Add the graphics to container and position/rotate
    graphics.setPosition(x, y);
    graphics.setRotation((rotation * Math.PI) / 180);
    graphics.setDepth(1);
    graphics.setData("shipPart", "sail");
    container.add(graphics);
  }

  /**
   * Add a billowed (curved) triangle sail for more realistic appearance
   */
  private addBillowedTriangleSail(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    width: number,
    height: number,
    color: number,
    rotation: number,
  ): void {
    const graphics = this.scene.add.graphics();
    graphics.fillStyle(color, 0.95);

    // Draw curved triangle sail with bezier curve for billowing effect
    const path = new Phaser.Curves.Path(0, height);
    path.lineTo(0, 0);

    // Curved billowing edge from top to bottom-right using spline
    path.splineTo([
      new Phaser.Math.Vector2(width * 0.3, height * 0.2),
      new Phaser.Math.Vector2(width * 0.5, height * 0.6),
      new Phaser.Math.Vector2(width, height),
    ]);

    path.closePath();
    graphics.fillPoints(path.getPoints(), true);

    // Add shading for depth
    graphics.lineStyle(1, 0xd4c5b0, 0.3);
    const shadingPath = new Phaser.Curves.Path(width * 0.2, height * 0.3);
    shadingPath.splineTo([
      new Phaser.Math.Vector2(width * 0.4, height * 0.35),
      new Phaser.Math.Vector2(width * 0.6, height * 0.55),
      new Phaser.Math.Vector2(width * 0.8, height * 0.8),
    ]);
    graphics.strokePoints(shadingPath.getPoints());

    graphics.setPosition(x, y);
    graphics.setRotation((rotation * Math.PI) / 180);
    graphics.setDepth(1);
    graphics.setData("shipPart", "sail");
    container.add(graphics);
  }

  /**
   * Add a billowed rectangle sail with curved edge
   */
  private addBillowedRectangleSail(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    width: number,
    height: number,
    color: number,
    rotation: number,
  ): void {
    const graphics = this.scene.add.graphics();
    graphics.fillStyle(color, 0.95);

    // Draw sail with curved billowing edge using path
    const path = new Phaser.Curves.Path(0, 0);
    path.lineTo(0, height);

    // Curved billowing bottom edge
    path.splineTo([
      new Phaser.Math.Vector2(width * 0.3, height + height * 0.1),
      new Phaser.Math.Vector2(width * 0.7, height + height * 0.1),
      new Phaser.Math.Vector2(width, height),
    ]);

    path.lineTo(width, 0);

    // Curved billowing top edge
    path.splineTo([
      new Phaser.Math.Vector2(width * 0.7, -height * 0.05),
      new Phaser.Math.Vector2(width * 0.3, -height * 0.05),
      new Phaser.Math.Vector2(0, 0),
    ]);

    path.closePath();
    graphics.fillPoints(path.getPoints(), true);

    // Add shading lines for billowing effect
    graphics.lineStyle(1, 0xd4c5b0, 0.2);
    for (let i = 1; i <= 3; i++) {
      const yPos = (height / 4) * i;
      const shadePath = new Phaser.Curves.Path(0, yPos);
      shadePath.splineTo([
        new Phaser.Math.Vector2(width * 0.3, yPos + height * 0.05),
        new Phaser.Math.Vector2(width * 0.7, yPos + height * 0.05),
        new Phaser.Math.Vector2(width, yPos),
      ]);
      graphics.strokePoints(shadePath.getPoints());
    }

    graphics.setPosition(x, y);
    graphics.setRotation((rotation * Math.PI) / 180);
    graphics.setDepth(1);
    graphics.setData("shipPart", "sail");
    container.add(graphics);
  }

  /**
   * Add curved hull using graphics for realistic ship shape
   */
  private addCurvedHull(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    width: number,
    height: number,
    color: number,
    shipType: string,
  ): void {
    const graphics = this.scene.add.graphics();
    graphics.fillStyle(color, 1);

    const halfWidth = width / 2;
    const halfHeight = height / 2;

    // Draw curved hull shape using path
    const path = new Phaser.Curves.Path(x - halfWidth, y);

    // Curve up to deck level
    path.splineTo([
      new Phaser.Math.Vector2(
        x - halfWidth + width * 0.1,
        y - halfHeight * 0.6,
      ),
      new Phaser.Math.Vector2(
        x - halfWidth + width * 0.3,
        y - halfHeight * 0.9,
      ),
      new Phaser.Math.Vector2(x, y - halfHeight),
    ]);

    // Continue to bow (right side)
    path.splineTo([
      new Phaser.Math.Vector2(
        x + halfWidth - width * 0.3,
        y - halfHeight * 0.9,
      ),
      new Phaser.Math.Vector2(
        x + halfWidth - width * 0.1,
        y - halfHeight * 0.6,
      ),
      new Phaser.Math.Vector2(x + halfWidth, y),
    ]);

    // Bottom curve (keel)
    path.splineTo([
      new Phaser.Math.Vector2(
        x + halfWidth - width * 0.2,
        y + halfHeight * 0.8,
      ),
      new Phaser.Math.Vector2(
        x - halfWidth + width * 0.2,
        y + halfHeight * 0.8,
      ),
      new Phaser.Math.Vector2(x - halfWidth, y),
    ]);

    path.closePath();
    graphics.fillPoints(path.getPoints(), true);

    // Add waterline effect
    graphics.lineStyle(2, 0x4a9fb5, 0.4);
    const waterPath = new Phaser.Curves.Path(
      x - halfWidth,
      y + halfHeight * 0.3,
    );
    waterPath.splineTo([
      new Phaser.Math.Vector2(
        x - halfWidth + width * 0.3,
        y + halfHeight * 0.4,
      ),
      new Phaser.Math.Vector2(
        x + halfWidth - width * 0.3,
        y + halfHeight * 0.4,
      ),
      new Phaser.Math.Vector2(x + halfWidth, y + halfHeight * 0.3),
    ]);
    graphics.strokePoints(waterPath.getPoints());

    container.add(graphics);
  }

  /**
   * Add railings along deck edge
   */
  private addRailings(
    container: Phaser.GameObjects.Container,
    deckWidth: number,
    deckHeight: number,
    deckY: number,
  ): void {
    const graphics = this.scene.add.graphics();
    graphics.lineStyle(1, 0x3d2817, 0.8);

    const halfWidth = deckWidth / 2;
    const railY = deckY - deckHeight / 2;
    const posts = 8; // Number of railing posts

    // Top rail
    graphics.beginPath();
    graphics.moveTo(-halfWidth, railY);
    graphics.lineTo(halfWidth, railY);
    graphics.strokePath();

    // Vertical posts
    for (let i = 0; i <= posts; i++) {
      const x = -halfWidth + (deckWidth / posts) * i;
      graphics.beginPath();
      graphics.moveTo(x, railY);
      graphics.lineTo(x, railY + deckHeight * 0.3);
      graphics.strokePath();
    }

    container.add(graphics);
  }

  /**
   * Add portholes (round windows) to cabin
   */
  private addPortholes(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    cabinWidth: number,
    count: number,
  ): void {
    const spacing = cabinWidth / (count + 1);
    const radius = Math.min(cabinWidth / (count * 3), 3);

    for (let i = 1; i <= count; i++) {
      const px = x - cabinWidth / 2 + spacing * i;
      const porthole = this.scene.add.circle(px, y, radius, 0x87ceeb, 0.6);
      container.add(porthole);

      // Add reflection effect
      const reflection = this.scene.add.circle(
        px - radius * 0.3,
        y - radius * 0.3,
        radius * 0.4,
        0xffffff,
        0.4,
      );
      container.add(reflection);
    }
  }

  /**
   * Add detailed figurehead at bow
   */
  private addFigurehead(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    size: number,
    faction?: string,
  ): void {
    const graphics = this.scene.add.graphics();

    // Faction-specific figurehead colors
    let figureColor = 0x8b6914; // Gold default
    if (faction === "navy") figureColor = 0x4169e1; // Royal blue
    if (faction === "pirate") figureColor = 0x8b0000; // Dark red
    if (faction === "merchant") figureColor = 0xdaa520; // Goldenrod

    // Draw stylized figurehead (serpent/dragon head shape) using path
    graphics.fillStyle(figureColor, 0.9);

    const path = new Phaser.Curves.Path(x, y);
    path.splineTo([
      new Phaser.Math.Vector2(x + size * 0.5, y - size * 0.8),
      new Phaser.Math.Vector2(x + size * 0.8, y - size * 0.5),
      new Phaser.Math.Vector2(x + size, y),
    ]);
    path.splineTo([
      new Phaser.Math.Vector2(x + size * 0.8, y + size * 0.5),
      new Phaser.Math.Vector2(x + size * 0.5, y + size * 0.8),
      new Phaser.Math.Vector2(x, y),
    ]);
    path.closePath();

    graphics.fillPoints(path.getPoints(), true);

    // Eye detail
    const eye = this.scene.add.circle(
      x + size * 0.7,
      y - size * 0.2,
      size * 0.1,
      0xff0000,
      0.8,
    );
    container.add(eye);

    container.add(graphics);
  }

  /**
   * Add rigging (ropes connecting mast to ship)
   */
  private addRigging(
    container: Phaser.GameObjects.Container,
    mastX: number,
    mastTop: number,
    shipWidth: number,
    shipHeight: number,
  ): void {
    const graphics = this.scene.add.graphics();
    graphics.lineStyle(0.8, 0x3d2817, 0.6);

    // Forestay (mast to bow)
    graphics.beginPath();
    graphics.moveTo(mastX, mastTop);
    graphics.lineTo(shipWidth / 2, shipHeight * 0.3);
    graphics.strokePath();

    // Backstay (mast to stern)
    graphics.beginPath();
    graphics.moveTo(mastX, mastTop);
    graphics.lineTo(-shipWidth / 2, shipHeight * 0.3);
    graphics.strokePath();

    // Shrouds (side supports)
    for (let i = 0; i < 3; i++) {
      const offset = mastTop + shipHeight * 0.5 * (i / 3);
      graphics.beginPath();
      graphics.moveTo(mastX, offset);
      graphics.lineTo(mastX - shipWidth * 0.15, shipHeight * 0.2);
      graphics.strokePath();

      graphics.beginPath();
      graphics.moveTo(mastX, offset);
      graphics.lineTo(mastX + shipWidth * 0.15, shipHeight * 0.2);
      graphics.strokePath();
    }

    container.add(graphics);
  }

  /**
   * Add yard (horizontal beam supporting sail)
   */
  private addYard(
    container: Phaser.GameObjects.Container,
    mastX: number,
    y: number,
    length: number,
  ): void {
    const graphics = this.scene.add.graphics();
    graphics.lineStyle(2, 0x3d2817, 1);

    graphics.beginPath();
    graphics.moveTo(mastX - length / 2, y);
    graphics.lineTo(mastX + length / 2, y);
    graphics.strokePath();

    // Add yard arm details (rope loops)
    for (let i = -2; i <= 2; i++) {
      const x = mastX + (length / 5) * i;
      graphics.fillStyle(0x6b5344, 0.8);
      graphics.fillCircle(x, y, 1.5);
    }

    container.add(graphics);
  }

  /**
   * Add detailed flag with faction emblem
   */
  private addDetailedFlag(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    width: number,
    height: number,
    color: number,
    faction?: string,
  ): void {
    // Create flag with waving effect using graphics
    const graphics = this.scene.add.graphics();
    graphics.fillStyle(color, 0.95);

    // Draw flag with wave using simple shape (complex waving can cause performance issues)
    const points: Phaser.Math.Vector2[] = [];

    const segments = 8;
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const px = x + width * t;
      const py = y + Math.sin(t * Math.PI * 2) * (height * 0.1);
      points.push(new Phaser.Math.Vector2(px, py));
    }

    for (let i = segments; i >= 0; i--) {
      const t = i / segments;
      const px = x + width * t;
      const py = y + height + Math.sin(t * Math.PI * 2) * (height * 0.1);
      points.push(new Phaser.Math.Vector2(px, py));
    }

    graphics.fillPoints(points, true);

    // Add faction emblem
    if (faction) {
      let emblemColor = 0xffffff;
      if (faction === "navy") emblemColor = 0xffd700;
      if (faction === "pirate") emblemColor = 0x000000;

      const emblem = this.scene.add.circle(
        x + width / 2,
        y + height / 2,
        Math.min(width, height) * 0.2,
        emblemColor,
        0.8,
      );
      container.add(emblem);
    }

    graphics.setData("shipPart", "flag");
    container.add(graphics);
  }

  /**
   * Add a rectangle sail at the specified position with rotation
   */
  private addRectangleSail(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    width: number,
    height: number,
    color: number,
    rotation: number,
    scale: number = 1.0,
  ): void {
    const sail = this.scene.add.rectangle(
      x,
      y,
      width * scale,
      height * scale,
      color,
    );
    sail.setFillStyle(color, 1);
    sail.setAlpha(0.95);
    sail.setRotation((rotation * Math.PI) / 180);
    sail.setData("shipPart", "sail");
    container.add(sail);
  }

  /**
   * Get faction-specific wood colors for hull variety
   */
  private getFactionWoodColors(
    faction?: string,
    shipId?: string,
  ): { hull: number; deck: number; cabin: number; cabin2: number } {
    // Base wood colors for non-faction ships
    let hullColors = [0x5c4033, 0x6b4d3a, 0x4f3a2a, 0x5a4231];
    let deckColors = [0x8b6f47, 0x9a7d52, 0x7d6340, 0x8a7249];
    let cabinColors = [0x6b5344, 0x7a5f4d, 0x5f4a3b, 0x6d5a48];
    let cabin2Colors = [0x5a4838, 0x694d3f, 0x4f3f2f, 0x5c4a36];

    // Faction-specific wood colors
    if (faction === "navy") {
      // Navy ships: darker, sturdier wood with blue-tinted weathering
      hullColors = [0x4a3a2a, 0x5a4a3a, 0x3a3a2a, 0x4a4a3a];
      deckColors = [0x7a6a5a, 0x8a7a6a, 0x6a5a4a, 0x7a6a5a];
      cabinColors = [0x5a4a3a, 0x6a5a4a, 0x4a3a2a, 0x5a4a3a];
      cabin2Colors = [0x4a3a2a, 0x5a4a3a, 0x3a3a2a, 0x4a3a2a];
    } else if (faction === "pirate") {
      // Pirate ships: weathered, dark wood with worn appearance
      hullColors = [0x3a2a1a, 0x4a3a2a, 0x2a2a1a, 0x3a3a2a];
      deckColors = [0x6a5a4a, 0x7a6a5a, 0x5a4a3a, 0x6a5a4a];
      cabinColors = [0x4a3a2a, 0x5a4a3a, 0x3a3a2a, 0x4a3a2a];
      cabin2Colors = [0x3a2a1a, 0x4a3a2a, 0x2a2a1a, 0x3a2a1a];
    } else if (faction === "bandit") {
      // Bandit ships: ramshackle, mismatched wood tones
      hullColors = [0x5a4a3a, 0x4a3a2a, 0x6a5a4a, 0x5a4a2a];
      deckColors = [0x8a7a6a, 0x7a6a5a, 0x9a8a7a, 0x8a7a5a];
      cabinColors = [0x6a5a4a, 0x5a4a3a, 0x7a6a5a, 0x6a5a3a];
      cabin2Colors = [0x5a4a3a, 0x4a3a2a, 0x6a5a4a, 0x5a4a2a];
    } else if (faction === "merchant") {
      // Merchant ships: well-maintained, polished wood
      hullColors = [0x6a5a4a, 0x7a6a5a, 0x5a5a4a, 0x6a6a5a];
      deckColors = [0x9a8a7a, 0xaa9a8a, 0x8a7a6a, 0x9a8a7a];
      cabinColors = [0x7a6a5a, 0x8a7a6a, 0x6a5a4a, 0x7a6a5a];
      cabin2Colors = [0x6a5a4a, 0x7a6a5a, 0x5a5a4a, 0x6a5a4a];
    }

    // Use ship ID for deterministic color selection
    const colorVar = shipId
      ? Math.abs(
          shipId.split("").reduce((h, c) => (h << 5) - h + c.charCodeAt(0), 0),
        ) % 4
      : 0;

    return {
      hull: hullColors[colorVar],
      deck: deckColors[colorVar],
      cabin: cabinColors[colorVar],
      cabin2: cabin2Colors[colorVar],
    };
  }

  /**
   * Get faction-specific sail colors and patterns
   * Navy: cream/white (traditional)
   * Pirate: bold and flamboyant (reds, blacks, golds)
   * Merchant: professional (earth tones, gold accents)
   * Bandit: chaotic (mismatched, wild colors)
   */
  private getFactionSailColors(
    faction?: string,
    shipId?: string,
  ): { main: number; stripe: number } {
    let mainColor = 0xf5f5dc; // Default cream/white
    let stripeColor = 0xe0d5b0; // Default light tan

    if (faction === "navy") {
      // Navy: traditional cream and light tan
      mainColor = 0xf5f5dc;
      stripeColor = 0xe0d5b0;
    } else if (faction === "pirate") {
      // Pirate: bold and varied - red, black, gold
      const sailVariants = [
        { main: 0xcc1111, stripe: 0xffdd00 }, // Red with gold stripe
        { main: 0x1a1a1a, stripe: 0xff6600 }, // Black with orange stripe
        { main: 0xdd0000, stripe: 0x222222 }, // Bright red with black stripe
        { main: 0x331111, stripe: 0xffaa00 }, // Dark red with gold
      ];
      const shipHash = shipId
        ? Math.abs(
            shipId
              .split("")
              .reduce((h, c) => (h << 5) - h + c.charCodeAt(0), 0),
          ) % sailVariants.length
        : 0;
      mainColor = sailVariants[shipHash].main;
      stripeColor = sailVariants[shipHash].stripe;
    } else if (faction === "merchant") {
      // Merchant: professional earth tones with gold accents
      const sailVariants = [
        { main: 0xd4a574, stripe: 0xffdd00 }, // Tan with gold
        { main: 0xc9956f, stripe: 0xeecc00 }, // Brown with gold
        { main: 0xe8d4b8, stripe: 0xffd700 }, // Light tan with bright gold
        { main: 0xb89968, stripe: 0xffea00 }, // Warm brown with gold
      ];
      const shipHash = shipId
        ? Math.abs(
            shipId
              .split("")
              .reduce((h, c) => (h << 5) - h + c.charCodeAt(0), 0),
          ) % sailVariants.length
        : 0;
      mainColor = sailVariants[shipHash].main;
      stripeColor = sailVariants[shipHash].stripe;
    } else if (faction === "bandit") {
      // Bandit: chaotic and mismatched
      const sailVariants = [
        { main: 0x9933ff, stripe: 0x00ff00 }, // Purple with neon green
        { main: 0xff00ff, stripe: 0x00ffff }, // Magenta with cyan
        { main: 0x00ffff, stripe: 0xff00ff }, // Cyan with magenta
        { main: 0xffff00, stripe: 0xff0000 }, // Yellow with red
        { main: 0xff6600, stripe: 0x0066ff }, // Orange with blue
        { main: 0x00ff66, stripe: 0xff0066 }, // Green with hot pink
      ];
      const shipHash = shipId
        ? Math.abs(
            shipId
              .split("")
              .reduce((h, c) => (h << 5) - h + c.charCodeAt(0), 0),
          ) % sailVariants.length
        : 0;
      mainColor = sailVariants[shipHash].main;
      stripeColor = sailVariants[shipHash].stripe;
    }

    return { main: mainColor, stripe: stripeColor };
  }

  private addPointyProw(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    h: number,
    baseColor: number,
  ): void {
    const c1 = baseColor;
    const c2 = 0x3d2817;
    const c3 = 0x4a3426;

    const base = this.scene.add.rectangle(x, y, w * 0.34, h * 0.42, c3);
    container.add(base);
    const mid = this.scene.add.rectangle(
      x + w * 0.14,
      y,
      w * 0.24,
      h * 0.3,
      c1,
    );
    container.add(mid);
    const tip = this.scene.add.rectangle(
      x + w * 0.26,
      y,
      w * 0.14,
      h * 0.18,
      c2,
    );
    container.add(tip);
  }

  /**
   * Add hull shading effect with darker top and lighter bottom for depth
   */
  private addHullShading(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    h: number,
    baseColor: number,
  ): void {
    // Darker top band (shadow effect)
    const darkTop = this.scene.add.rectangle(
      x,
      y - h * 0.35,
      w * 1.8,
      h * 0.5,
      0x3d2817,
    );
    darkTop.setAlpha(0.2);
    container.addAt(darkTop, 1); // Add after hull base but before other elements

    // Waterline - lighter band at bottom
    const waterline = this.scene.add.rectangle(
      x,
      y + h * 0.35,
      w * 1.8,
      h * 0.2,
      0x4a9fb5,
    );
    waterline.setAlpha(0.25);
    container.addAt(waterline, 2);
  }

  /**
   * Add hull shading to brigantine-sized ships
   */
  private addHullShadingLarge(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    h: number,
    baseColor: number,
  ): void {
    const darkTop = this.scene.add.rectangle(
      x,
      y - h * 0.4,
      w * 2.0,
      h * 0.6,
      0x3d2817,
    );
    darkTop.setAlpha(0.2);
    container.addAt(darkTop, 1);

    const waterline = this.scene.add.rectangle(
      x,
      y + h * 0.4,
      w * 2.0,
      h * 0.25,
      0x4a9fb5,
    );
    waterline.setAlpha(0.25);
    container.addAt(waterline, 2);
  }

  /**
   * Add hull shading to frigate-sized ships
   */
  private addHullShadingXL(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    h: number,
    baseColor: number,
  ): void {
    const darkTop = this.scene.add.rectangle(
      x,
      y - h * 0.45,
      w * 2.2,
      h * 0.7,
      0x3d2817,
    );
    darkTop.setAlpha(0.2);
    container.addAt(darkTop, 1);

    const waterline = this.scene.add.rectangle(
      x,
      y + h * 0.45,
      w * 2.2,
      h * 0.3,
      0x4a9fb5,
    );
    waterline.setAlpha(0.25);
    container.addAt(waterline, 2);
  }

  /**
   * Add hull shading to galleon-sized ships
   */
  private addHullShadingGalleon(
    container: Phaser.GameObjects.Container,
    x: number,
    y: number,
    w: number,
    h: number,
    baseColor: number,
  ): void {
    const darkTop = this.scene.add.rectangle(
      x,
      y - h * 0.5,
      w * 2.4,
      h * 0.8,
      0x3d2817,
    );
    darkTop.setAlpha(0.2);
    container.addAt(darkTop, 1);

    const waterline = this.scene.add.rectangle(
      x,
      y + h * 0.5,
      w * 2.4,
      h * 0.35,
      0x4a9fb5,
    );
    waterline.setAlpha(0.25);
    container.addAt(waterline, 2);
  }

  /**
   * Sloop - small, fast, single-masted ship
   * Pixel art style: blocky hull, clear deck details, simple shapes
   */
  private createSloopVisual(
    container: Phaser.GameObjects.Container,
    w: number,
    h: number,
    color: number,
    shipId?: string,
    faction?: string,
  ): void {
    const factionColors = this.getFactionWoodColors(faction, shipId);
    const sailColors = this.getFactionSailColors(faction, shipId);
    const hullColor = factionColors.hull;
    const deckColor = factionColors.deck;
    const cabinColor = factionColors.cabin;
    const darkWood = 0x3d2817;

    // Hull with improved shaping - tapered from stern to bow
    const hull = this.scene.add.rectangle(0, 0, w * 2.0, h * 1.0, hullColor);
    container.add(hull);

    // Hull bow taper - narrower at front (use rectangle instead of polygon)
    const bowTaper = this.scene.add.rectangle(
      w * 1.075,
      0,
      w * 0.15,
      h * 0.7,
      hullColor,
    );
    container.add(bowTaper);

    // Hull stern curve - rounder at back (use rectangle instead of polygon)
    const stern = this.scene.add.rectangle(
      -w * 0.925,
      0,
      w * 0.15,
      h * 0.75,
      hullColor,
    );
    container.add(stern);

    // Hull shadow (bottom half darker)
    const hullShadow = this.scene.add.rectangle(
      0,
      h * 0.3,
      w * 2.0,
      h * 0.4,
      darkWood,
    );
    hullShadow.setAlpha(0.3);
    container.add(hullShadow);

    // Deck - clear rectangular platform
    const deck = this.scene.add.rectangle(
      0,
      -h * 0.3,
      w * 1.7,
      h * 0.5,
      deckColor,
    );
    container.add(deck);

    // Deck planks (horizontal stripes for pixel detail)
    for (let i = 0; i < 3; i++) {
      const plank = this.scene.add.rectangle(
        0,
        -h * 0.3 + i * h * 0.15 - h * 0.15,
        w * 1.7,
        1,
        0x6d4c41,
      );
      container.add(plank);
    }

    // Cabin/Quarterdeck - solid block with slight taper
    const cabin = this.scene.add.rectangle(
      -w * 0.4,
      0,
      w * 0.8,
      h * 0.6,
      cabinColor,
    );
    container.add(cabin);

    // Cabin roof detail - slightly raised section
    const cabinRoof = this.scene.add.rectangle(
      -w * 0.4,
      -h * 0.25,
      w * 0.7,
      h * 0.15,
      0x6d5344,
    );
    container.add(cabinRoof);

    // Cabin windows - two square portholes
    const porthole1 = this.scene.add.rectangle(
      -w * 0.6,
      0,
      w * 0.15,
      h * 0.15,
      0x4a9fb5,
    );
    container.add(porthole1);

    const porthole2 = this.scene.add.rectangle(
      -w * 0.2,
      0,
      w * 0.15,
      h * 0.15,
      0x4a9fb5,
    );
    container.add(porthole2);

    // Bowsprit - stick-like horizontal beam extending from bow
    const bowsprit = this.scene.add.rectangle(
      w * 0.9,
      -h * 0.15,
      w * 0.6,
      2,
      darkWood,
    );
    container.add(bowsprit);

    // Bow reinforcement - small wooden block at bow
    const bowReinforcement = this.scene.add.rectangle(
      w * 1.05,
      0,
      w * 0.2,
      h * 0.5,
      darkWood,
    );
    container.add(bowReinforcement);

    // Mast - simple vertical rectangle
    const mastHeight = h * 1.3;
    const mastY = -h * 0.3 - mastHeight / 2;
    const mast = this.scene.add.rectangle(0, mastY, 3, mastHeight, darkWood);
    container.add(mast);

    // Sail - rectangle with faction-specific colors
    const sailY = mastY - h * 0.1;
    const sail = this.scene.add.rectangle(
      -w * 0.3,
      sailY,
      w * 0.9,
      h * 1.0,
      sailColors.main,
    );
    container.add(sail);

    // Sail horizontal stripe for detail
    const sailStripe = this.scene.add.rectangle(
      -w * 0.3,
      sailY,
      w * 0.9,
      h * 0.2,
      sailColors.stripe,
    );
    container.add(sailStripe);

    // Yard (horizontal beam) - simple rectangle
    const yard = this.scene.add.rectangle(
      0,
      sailY - h * 0.5,
      w * 1.0,
      2,
      darkWood,
    );
    container.add(yard);

    // Flag at top - simple rectangular pennant (more pixelated)
    const flagY = mastY - mastHeight / 2 - h * 0.1;
    const flag = this.scene.add.rectangle(
      w * 0.2,
      flagY,
      w * 0.35,
      h * 0.2,
      color,
    );
    container.add(flag);
  }

  /**
   * Raft - primitive vessel made of logs lashed together
   * Pixel art style: blocky, simple shapes, clear definition
   */
  private createRaftVisual(
    container: Phaser.GameObjects.Container,
    w: number,
    h: number,
    color: number,
    shipId?: string,
    faction?: string,
  ): void {
    // Player barrel - tighter, rounded pixel-art silhouette
    const barrelColor = 0x9a7b62;
    const barrelDarkColor = 0x6d4f3a;
    const bandColor = 0x5b4636;
    const bandHighlight = 0xb08a6f;
    const borderColor = 0x3d2817; // Darker wooden border
    const barrelWidth = w * 0.9;
    const barrelHeight = h * 1.4;
    const bodyHeight = barrelHeight * 0.7;
    const bandHeight = barrelHeight * 0.12;
    const bodyY = -bodyHeight * 0.05;
    const borderThickness = 1;

    const body = this.scene.add.rectangle(
      0,
      bodyY,
      barrelWidth,
      bodyHeight,
      barrelColor,
    );
    body.setStrokeStyle(borderThickness, borderColor);
    container.add(body);

    const topCap = this.scene.add.rectangle(
      0,
      bodyY - bodyHeight * 0.55,
      barrelWidth * 0.8,
      bandHeight,
      barrelColor,
    );
    topCap.setStrokeStyle(borderThickness, borderColor);
    container.add(topCap);

    const bottomCap = this.scene.add.rectangle(
      0,
      bodyY + bodyHeight * 0.55,
      barrelWidth * 0.85,
      bandHeight,
      barrelColor,
    );
    bottomCap.setStrokeStyle(borderThickness, borderColor);
    container.add(bottomCap);

    const bandTop = this.scene.add.rectangle(
      0,
      bodyY - bandHeight * 0.6,
      barrelWidth,
      bandHeight,
      bandColor,
    );
    container.add(bandTop);

    const bandMid = this.scene.add.rectangle(
      0,
      bodyY + bandHeight * 0.9,
      barrelWidth,
      bandHeight,
      bandColor,
    );
    container.add(bandMid);

    const bandHighlightTop = this.scene.add.rectangle(
      0,
      bodyY - bandHeight * 0.85,
      barrelWidth,
      bandHeight * 0.4,
      bandHighlight,
    );
    bandHighlightTop.setAlpha(0.6);
    container.add(bandHighlightTop);

    const bandHighlightMid = this.scene.add.rectangle(
      0,
      bodyY + bandHeight * 0.65,
      barrelWidth,
      bandHeight * 0.4,
      bandHighlight,
    );
    bandHighlightMid.setAlpha(0.6);
    container.add(bandHighlightMid);

    const barrelShade = this.scene.add.rectangle(
      -barrelWidth * 0.35,
      bodyY,
      barrelWidth * 0.18,
      bodyHeight * 0.9,
      barrelDarkColor,
    );
    barrelShade.setAlpha(0.5);
    container.add(barrelShade);

    const stopper = this.scene.add.rectangle(
      0,
      bodyY - bodyHeight * 0.75,
      barrelWidth * 0.18,
      bandHeight * 0.9,
      0x4f3a2b,
    );
    container.add(stopper);
  }

  /**
   * Brigantine - medium, two-masted ship
   * Pixel art style: larger blocky hull, two clear masts, deck details
   */
  private createBrigantineVisual(
    container: Phaser.GameObjects.Container,
    w: number,
    h: number,
    color: number,
    shipId?: string,
    faction?: string,
  ): void {
    const factionColors = this.getFactionWoodColors(faction, shipId);
    const hullColor = factionColors.hull;
    const deckColor = factionColors.deck;
    const cabinColor = factionColors.cabin;
    const darkWood = 0x3d2817;

    // Main hull with tapered bow and stern
    const hull = this.scene.add.rectangle(0, 0, w * 2.2, h * 1.2, hullColor);
    container.add(hull);

    // Bow taper (use rectangle instead of polygon)
    const bowTaper = this.scene.add.rectangle(
      w * 1.2,
      0,
      w * 0.2,
      h * 1.0,
      hullColor,
    );
    container.add(bowTaper);

    // Stern curve (use rectangle instead of polygon)
    const stern = this.scene.add.rectangle(
      -w * 1.025,
      0,
      w * 0.15,
      h * 1.1,
      hullColor,
    );
    container.add(stern);

    // Hull shadow (bottom darker)
    const hullShadow = this.scene.add.rectangle(
      0,
      h * 0.4,
      w * 2.2,
      h * 0.4,
      darkWood,
    );
    hullShadow.setAlpha(0.3);
    container.add(hullShadow);

    // Deck - wider platform
    const deck = this.scene.add.rectangle(
      0,
      -h * 0.2,
      w * 2.0,
      h * 0.6,
      deckColor,
    );
    container.add(deck);

    // Deck planks
    for (let i = 0; i < 4; i++) {
      const plank = this.scene.add.rectangle(
        0,
        -h * 0.2 + i * h * 0.13 - h * 0.2,
        w * 2.0,
        1,
        0x6d4c41,
      );
      container.add(plank);
    }

    // Cabin - larger quarterdeck with raised roof
    const cabin = this.scene.add.rectangle(
      -w * 0.5,
      h * 0.1,
      w * 1.0,
      h * 0.7,
      cabinColor,
    );
    container.add(cabin);

    // Cabin roof - raised detail
    const cabinRoof = this.scene.add.rectangle(
      -w * 0.5,
      -h * 0.15,
      w * 0.9,
      h * 0.2,
      0x6d5344,
    );
    container.add(cabinRoof);

    // Three portholes on cabin
    for (let i = 0; i < 3; i++) {
      const porthole = this.scene.add.rectangle(
        -w * 0.8 + i * w * 0.3,
        h * 0.1,
        w * 0.15,
        h * 0.15,
        0x4a9fb5,
      );
      container.add(porthole);
    }

    // Bowsprit - stick-like beam extending from bow
    const bowsprit = this.scene.add.rectangle(
      w * 1.05,
      -h * 0.1,
      w * 0.7,
      2,
      darkWood,
    );
    container.add(bowsprit);

    // Bow reinforcement - wooden block at bow
    const bowReinforcement = this.scene.add.rectangle(
      w * 1.15,
      0,
      w * 0.25,
      h * 0.6,
      darkWood,
    );
    container.add(bowReinforcement);

    // Two masts - fore and main
    const mastHeight1 = h * 1.4;
    const mastHeight2 = h * 1.3;

    // Foremast (front)
    const mast1Y = -h * 0.2 - mastHeight1 / 2;
    const mast1 = this.scene.add.rectangle(
      w * 0.4,
      mast1Y,
      3,
      mastHeight1,
      darkWood,
    );
    container.add(mast1);

    // Mainmast (back)
    const mast2Y = -h * 0.2 - mastHeight2 / 2;
    const mast2 = this.scene.add.rectangle(
      -w * 0.3,
      mast2Y,
      3,
      mastHeight2,
      darkWood,
    );
    container.add(mast2);

    // Fore sail
    const sail1Y = mast1Y;
    const sail1 = this.scene.add.rectangle(
      w * 0.1,
      sail1Y,
      w * 0.9,
      h * 1.1,
      0xf5f5dc,
    );
    container.add(sail1);

    const sail1Stripe = this.scene.add.rectangle(
      w * 0.1,
      sail1Y,
      w * 0.9,
      h * 0.2,
      0xe0d5b0,
    );
    container.add(sail1Stripe);

    // Main sail
    const sail2Y = mast2Y;
    const sail2 = this.scene.add.rectangle(
      -w * 0.6,
      sail2Y,
      w * 0.85,
      h * 1.0,
      0xf5f5dc,
    );
    container.add(sail2);

    const sail2Stripe = this.scene.add.rectangle(
      -w * 0.6,
      sail2Y,
      w * 0.85,
      h * 0.2,
      0xe0d5b0,
    );
    container.add(sail2Stripe);

    // Yards
    const yard1 = this.scene.add.rectangle(
      w * 0.4,
      mast1Y - h * 0.55,
      w * 1.0,
      2,
      darkWood,
    );
    container.add(yard1);

    const yard2 = this.scene.add.rectangle(
      -w * 0.3,
      mast2Y - h * 0.5,
      w * 0.95,
      2,
      darkWood,
    );
    container.add(yard2);

    // Flags - rectangular pennants
    const flag1 = this.scene.add.rectangle(
      w * 0.5,
      mast1Y - mastHeight1 / 2 - h * 0.1,
      w * 0.4,
      h * 0.2,
      color,
    );
    container.add(flag1);

    const flag2 = this.scene.add.rectangle(
      -w * 0.1,
      mast2Y - mastHeight2 / 2 - h * 0.1,
      w * 0.4,
      h * 0.2,
      color,
    );
    container.add(flag2);
  }

  /**
   * Frigate - large, three-masted warship
   */
  /**
   * Frigate - large, three-masted warship
   * Pixel art style: imposing hull, three masts, crow's nest, deck cannons
   */
  private createFrigateVisual(
    container: Phaser.GameObjects.Container,
    w: number,
    h: number,
    color: number,
    shipId?: string,
    faction?: string,
  ): void {
    const factionColors = this.getFactionWoodColors(faction, shipId);
    const hullColor = factionColors.hull;
    const deckColor = factionColors.deck;
    const cabinColor = factionColors.cabin;
    const darkWood = 0x3d2817;

    // Large hull
    const hull = this.scene.add.rectangle(0, 0, w * 2.5, h * 1.4, hullColor);
    container.add(hull);

    // Hull shadow
    const hullShadow = this.scene.add.rectangle(
      0,
      h * 0.5,
      w * 2.5,
      h * 0.45,
      darkWood,
    );
    hullShadow.setAlpha(0.3);
    container.add(hullShadow);

    // Deck
    const deck = this.scene.add.rectangle(
      0,
      -h * 0.3,
      w * 2.3,
      h * 0.7,
      deckColor,
    );
    container.add(deck);

    // Deck planks
    for (let i = 0; i < 5; i++) {
      const plank = this.scene.add.rectangle(
        0,
        -h * 0.3 + i * h * 0.12 - h * 0.24,
        w * 2.3,
        1,
        0x6d4c41,
      );
      container.add(plank);
    }

    // Large cabin with raised castle
    const cabin = this.scene.add.rectangle(
      -w * 0.7,
      h * 0.1,
      w * 1.2,
      h * 0.8,
      cabinColor,
    );
    container.add(cabin);

    // Cabin roof - raised detail
    const cabinRoof = this.scene.add.rectangle(
      -w * 0.7,
      -h * 0.15,
      w * 1.1,
      h * 0.25,
      0x6d5344,
    );
    container.add(cabinRoof);

    // Four portholes
    for (let i = 0; i < 4; i++) {
      const porthole = this.scene.add.rectangle(
        -w * 1.1 + i * w * 0.3,
        h * 0.1,
        w * 0.15,
        h * 0.15,
        0x4a9fb5,
      );
      container.add(porthole);
    }

    // Bowsprit - prominent stick-like beam
    const bowsprit = this.scene.add.rectangle(
      w * 1.15,
      -h * 0.05,
      w * 0.85,
      2,
      darkWood,
    );
    container.add(bowsprit);

    // Bow reinforcement - substantial wooden block at bow
    const bowReinforcement = this.scene.add.rectangle(
      w * 1.3,
      0,
      w * 0.3,
      h * 0.7,
      darkWood,
    );
    container.add(bowReinforcement);

    // Three masts
    const mast1 = this.scene.add.rectangle(
      w * 0.6,
      -h * 0.3 - h * 0.7,
      3,
      h * 1.5,
      darkWood,
    );
    container.add(mast1);

    const mast2 = this.scene.add.rectangle(
      0,
      -h * 0.3 - h * 0.75,
      4,
      h * 1.6,
      darkWood,
    );
    container.add(mast2);

    const mast3 = this.scene.add.rectangle(
      -w * 0.5,
      -h * 0.3 - h * 0.65,
      3,
      h * 1.4,
      darkWood,
    );
    container.add(mast3);

    // Three sails
    const sail1 = this.scene.add.rectangle(
      w * 0.3,
      -h * 0.8,
      w * 0.95,
      h * 1.2,
      0xf5f5dc,
    );
    container.add(sail1);

    const sail1Stripe = this.scene.add.rectangle(
      w * 0.3,
      -h * 0.8,
      w * 0.95,
      h * 0.25,
      0xe0d5b0,
    );
    container.add(sail1Stripe);

    const sail2 = this.scene.add.rectangle(
      -w * 0.3,
      -h * 0.85,
      w * 1.0,
      h * 1.3,
      0xf5f5dc,
    );
    container.add(sail2);

    const sail2Stripe = this.scene.add.rectangle(
      -w * 0.3,
      -h * 0.85,
      w * 1.0,
      h * 0.25,
      0xe0d5b0,
    );
    container.add(sail2Stripe);

    const sail3 = this.scene.add.rectangle(
      -w * 0.8,
      -h * 0.75,
      w * 0.9,
      h * 1.15,
      0xf5f5dc,
    );
    container.add(sail3);

    const sail3Stripe = this.scene.add.rectangle(
      -w * 0.8,
      -h * 0.75,
      w * 0.9,
      h * 0.25,
      0xe0d5b0,
    );
    container.add(sail3Stripe);

    // Crow's nest on main mast - circular platform
    const crowsNest = this.scene.add.circle(
      0,
      -h * 0.3 - h * 1.05,
      w * 0.2,
      darkWood,
    );
    container.add(crowsNest);

    // Flags - rectangular pennants
    const flag1 = this.scene.add.rectangle(
      w * 0.7,
      -h * 1.2,
      w * 0.4,
      h * 0.2,
      color,
    );
    container.add(flag1);

    const flag2 = this.scene.add.rectangle(
      w * 0.1,
      -h * 1.3,
      w * 0.4,
      h * 0.2,
      color,
    );
    container.add(flag2);

    const flag3 = this.scene.add.rectangle(
      -w * 0.4,
      -h * 1.15,
      w * 0.4,
      h * 0.2,
      color,
    );
    container.add(flag3);
  }

  /**
   * Galleon - massive, four-masted ship
   * Pixel art style: largest ship with distinct masts, multi-deck cabin, detailed rigging
   */
  private createGalleonVisual(
    container: Phaser.GameObjects.Container,
    w: number,
    h: number,
    color: number,
    shipId?: string,
    faction?: string,
  ): void {
    const factionColors = this.getFactionWoodColors(faction, shipId);
    const hullColor = factionColors.hull;
    const deckColor = factionColors.deck;
    const cabinColor = factionColors.cabin;
    const darkWood = 0x3d2817;
    const lightWood = 0x8b6f47;

    // Massive hull with gradient effect
    const hull = this.scene.add.rectangle(0, 0, w * 2.8, h * 1.6, hullColor);
    container.add(hull);

    // Hull waterline definition
    const waterline = this.scene.add.rectangle(
      0,
      h * 0.4,
      w * 2.8,
      2,
      0x1a1a1a,
    );
    waterline.setAlpha(0.5);
    container.add(waterline);

    // Hull shadow/depth
    const hullShadow = this.scene.add.rectangle(
      0,
      h * 0.7,
      w * 2.8,
      h * 0.4,
      darkWood,
    );
    hullShadow.setAlpha(0.25);
    container.add(hullShadow);

    // Main deck with clear definition
    const deck = this.scene.add.rectangle(
      0,
      -h * 0.3,
      w * 2.6,
      h * 0.75,
      deckColor,
    );
    container.add(deck);

    // Deck edge definition
    const deckEdge = this.scene.add.rectangle(
      0,
      -h * 0.67,
      w * 2.6,
      1,
      darkWood,
    );
    deckEdge.setAlpha(0.6);
    container.add(deckEdge);

    // Deck planks for texture
    for (let i = 0; i < 5; i++) {
      const plank = this.scene.add.rectangle(
        0,
        -h * 0.3 + i * h * 0.15,
        w * 2.6,
        1,
        0x6d4c41,
      );
      plank.setAlpha(0.4);
      container.add(plank);
    }

    // Forecastle (bow castle) - raised platform at front
    const forecastle = this.scene.add.rectangle(
      w * 0.6,
      -h * 0.15,
      w * 0.8,
      h * 0.5,
      cabinColor,
    );
    container.add(forecastle);

    const forecastleEdge = this.scene.add.rectangle(
      w * 0.6,
      -h * 0.4,
      w * 0.8,
      1,
      darkWood,
    );
    forecastleEdge.setAlpha(0.6);
    container.add(forecastleEdge);

    // Sterncastle (aft castle) - two-level structure
    const sterncastle1 = this.scene.add.rectangle(
      -w * 0.8,
      h * 0.15,
      w * 1.2,
      h * 0.7,
      cabinColor,
    );
    container.add(sterncastle1);

    const sterncastle2 = this.scene.add.rectangle(
      -w * 0.8,
      h * 0.55,
      w * 0.9,
      h * 0.5,
      0x6d5344,
    );
    container.add(sterncastle2);

    // Sterncastle roof with definition
    const sterncaslteRoof = this.scene.add.rectangle(
      -w * 0.8,
      -h * 0.05,
      w * 1.3,
      h * 0.2,
      0x5a4a39,
    );
    container.add(sterncaslteRoof);

    // Portholes on sterncastle
    for (let i = 0; i < 5; i++) {
      const porthole = this.scene.add.rectangle(
        -w * 1.35 + i * w * 0.28,
        h * 0.15,
        w * 0.12,
        h * 0.12,
        0x2a5f7f,
      );
      porthole.setStrokeStyle(1, 0x1a1a1a, 0.8);
      container.add(porthole);
    }

    // Prominent bowsprit
    const bowsprit = this.scene.add.rectangle(
      w * 1.35,
      -h * 0.08,
      w * 1.1,
      2.5,
      darkWood,
    );
    container.add(bowsprit);

    // Bow reinforcement
    const bowReinforcement = this.scene.add.rectangle(
      w * 1.5,
      0,
      w * 0.3,
      h * 0.7,
      darkWood,
    );
    container.add(bowReinforcement);

    // Four distinct masts with better spacing
    const mastPositions = [w * 1.0, w * 0.35, -w * 0.35, -w * 1.0];
    const mastHeights = [h * 1.55, h * 1.75, h * 1.75, h * 1.5];
    const mastWidths = [3.5, 4.5, 4.5, 3.5];

    for (let i = 0; i < 4; i++) {
      // Main mast shaft
      const mast = this.scene.add.rectangle(
        mastPositions[i],
        -h * 0.3 - mastHeights[i] / 2,
        mastWidths[i],
        mastHeights[i],
        darkWood,
      );
      container.add(mast);

      // Mast highlight for definition
      const mastHighlight = this.scene.add.rectangle(
        mastPositions[i] - mastWidths[i] / 3,
        -h * 0.3 - mastHeights[i] / 2,
        mastWidths[i] * 0.4,
        mastHeights[i],
        lightWood,
      );
      mastHighlight.setAlpha(0.3);
      container.add(mastHighlight);

      // Mast bands (reinforcement rings)
      for (let j = 0; j < 3; j++) {
        const bandY =
          -h * 0.3 - mastHeights[i] / 2 + (mastHeights[i] / 4) * (j + 1);
        const band = this.scene.add.rectangle(
          mastPositions[i],
          bandY,
          mastWidths[i] + 2,
          1.5,
          0x2a2a2a,
        );
        band.setAlpha(0.6);
        container.add(band);
      }
    }

    // Four sails with better definition - positioned higher on masts
    const sailData = [
      { x: w * 0.7, y: -h * 1.1, w: w * 0.95, h: h * 1.25 },
      { x: w * 0.1, y: -h * 1.15, w: w * 1.0, h: h * 1.35 },
      { x: -w * 0.5, y: -h * 1.15, w: w * 1.0, h: h * 1.35 },
      { x: -w * 1.0, y: -h * 1.05, w: w * 0.9, h: h * 1.2 },
    ];

    sailData.forEach((sail) => {
      // Main sail
      const sailRect = this.scene.add.rectangle(
        sail.x,
        sail.y,
        sail.w,
        sail.h,
        0xf5f5dc,
      );
      sailRect.setAlpha(0.92);
      container.add(sailRect);

      // Sail shadow/shading
      const sailShade = this.scene.add.rectangle(
        sail.x + sail.w * 0.15,
        sail.y,
        sail.w * 0.3,
        sail.h,
        0xe8dcc8,
      );
      sailShade.setAlpha(0.5);
      container.add(sailShade);

      // Sail stripe/seam
      const sailStripe = this.scene.add.rectangle(
        sail.x,
        sail.y - sail.h * 0.3,
        sail.w,
        1.5,
        0xd4c5a0,
      );
      sailStripe.setAlpha(0.7);
      container.add(sailStripe);
    });

    // Flag on main mast only - distinctive pennant
    const mainMastIndex = 1; // Second mast is the main mast
    const flagY = -h * 0.3 - mastHeights[mainMastIndex] - h * 0.2;
    const flag = this.scene.add.rectangle(
      mastPositions[mainMastIndex],
      flagY,
      w * 0.6,
      h * 0.25,
      color,
    );
    flag.setAlpha(0.95);
    flag.setStrokeStyle(1, 0x1a1a1a, 0.6);
    container.add(flag);
  }

  /**
   * Add gentle bobbing motion animation to a ship
   */
  private addBobbingAnimation(ship: ShipSprite): void {
    // Stop any existing bobbing tween
    if (ship.bobbingTween) {
      ship.bobbingTween.stop();
    }

    // Create gentle bobbing animation (2-3px bob on 2-second cycle)
    const bobDistance = 2.5;
    ship.bobbingTween = this.scene.tweens.add({
      targets: ship,
      y: ship.y + bobDistance,
      duration: 2000,
      ease: "Sine.inOut",
      yoyo: true,
      repeat: -1,
      paused: false,
    });
  }

  /**
   * Create wake trail effect behind moving ship
   * Animated circles that expand and fade at the stern
   */
  private createWakeTrail(
    ship: ShipSprite,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
  ): void {
    const distance = Math.hypot(toX - fromX, toY - fromY);
    const wakeCount = Math.max(1, Math.floor(distance / 12)); // Wake every 12px
    const wakeColor = 0x88ccff;

    for (let i = 1; i <= Math.min(wakeCount, 2); i++) {
      const progress = i / (wakeCount + 1);
      const wakeX = fromX + (toX - fromX) * progress;
      const wakeY = fromY + (toY - fromY) * progress;

      // Create expanding wake circle with fade - NEGATIVE depth to ensure it's behind everything
      const wake = this.scene.add.circle(wakeX, wakeY, 3, wakeColor);
      wake.setFillStyle(wakeColor, 0.1);
      wake.setDepth(-3); // Negative depth to render behind ocean grid (which is at -5 to -10)

      // Animate wake expansion and fade
      this.scene.tweens.add({
        targets: wake,
        radius: 12,
        alpha: 0,
        duration: 700,
        ease: "Quad.easeOut",
        onComplete: () => {
          wake.destroy();
        },
      });
    }
  }

  /**
   * Update ship color based on type
   */
  private updateShipColor(ship: ShipSprite, data: ShipData): void {
    const hull = ship.hull;

    // Check if hull is a container (ship visual) or a shape (circle/square)
    const isContainer = "getAt" in hull;

    if (data.ownerType === "npc") {
      // NPCs can be either shapes (in sanctums) or containers (in ocean)
      if (isContainer) {
        // NPC ship visual in ocean - update the flag (last child)
        const container = hull as unknown as Phaser.GameObjects.Container;
        const flag = (this.findShipPart(container, "flag") ||
          container.getAt(container.length - 1)) as any;
        if (flag && flag.setFillStyle) {
          const npcColor = data.faction
            ? NPC_FACTION_COLORS[data.faction] || COLORS.npcShip
            : data.behaviorType
              ? NPC_BEHAVIOR_COLORS[data.behaviorType] || COLORS.npcShip
              : COLORS.npcShip;
          flag.setFillStyle(npcColor);
        }
      } else {
        // NPC circle in sanctum - update directly
        if (data.faction) {
          (hull as any).setFillStyle(
            NPC_FACTION_COLORS[data.faction] || COLORS.npcShip,
          );
        } else if (data.behaviorType) {
          (hull as any).setFillStyle(
            NPC_BEHAVIOR_COLORS[data.behaviorType] || COLORS.npcShip,
          );
        }
      }
    } else if (data.shipColor) {
      // Player ships - update only the flag (last child)
      const playerColor = parseInt(data.shipColor.replace("#", ""), 16);
      if (isContainer) {
        const container = hull as unknown as Phaser.GameObjects.Container;
        // Flag is the last child (index -1)
        const flag = (this.findShipPart(container, "flag") ||
          container.getAt(container.length - 1)) as any;
        if (flag && flag.setFillStyle) {
          flag.setFillStyle(playerColor);
        }
      }
    }
  }

  /**
   * Get ship color based on type and faction
   */
  private getShipColor(
    data: ShipData,
    isLocal: boolean,
    isNPC: boolean,
  ): number {
    if (isLocal) {
      return data.shipColor
        ? parseInt(data.shipColor.replace("#", ""), 16)
        : COLORS.playerShip;
    } else if (!isNPC && data.shipColor) {
      return parseInt(data.shipColor.replace("#", ""), 16);
    } else if (isNPC && data.faction) {
      return NPC_FACTION_COLORS[data.faction] || COLORS.npcShip;
    } else if (isNPC && data.behaviorType) {
      return NPC_BEHAVIOR_COLORS[data.behaviorType] || COLORS.npcShip;
    } else if (isNPC) {
      return COLORS.npcShip;
    }
    return COLORS.allyShip;
  }

  /**
   * Show tooltip for a ship - emits event for React component
   */
  showShipTooltip(ship: ShipSprite): void {
    this.hideShipTooltip();

    const data = ship.shipData;
    const camera = this.scene.cameras.main;
    const screenX = ship.x - camera.scrollX;
    const screenY = ship.y - camera.scrollY;

    const tooltipWidth = 140;
    const isNPC = data.ownerType === "npc";
    const hasBounty = isNPC && data.bounty && data.bounty > 0;
    const tooltipHeight = isNPC ? (hasBounty ? 100 : 85) : 70;
    let tooltipX = screenX + 25;
    let tooltipY = screenY - 10;

    // Keep tooltip within bounds
    if (tooltipX + tooltipWidth > camera.width) {
      tooltipX = screenX - tooltipWidth - 10;
    }
    if (tooltipY + tooltipHeight > camera.height) {
      tooltipY = camera.height - tooltipHeight - 5;
    }
    if (tooltipY < 5) {
      tooltipY = 5;
    }

    // Get portrait index for NPCs
    const portraitIndex = isNPC ? this.getPortraitIndex(data.id) : undefined;

    // Emit event for React component to render tooltip
    this.scene.events.emit("ship:tooltip:show", {
      x: tooltipX,
      y: tooltipY,
      shipData: data,
      shipPosition: { x: ship.x, y: ship.y },
      portraitIndex,
    });
  }

  hideShipTooltip(): void {
    // Emit event to hide React tooltip
    this.scene.events.emit("ship:tooltip:hide");
  }

  private getTooltipBorderColor(
    data: ShipData,
    isLocal: boolean,
    isNPC: boolean,
  ): number {
    if (isLocal) {
      return data.shipColor
        ? parseInt(data.shipColor.replace("#", ""), 16)
        : 0x2ecc71;
    } else if (isNPC && data.faction) {
      return NPC_FACTION_COLORS[data.faction] || 0xf39c12;
    } else if (isNPC && data.behaviorType) {
      return NPC_BEHAVIOR_COLORS[data.behaviorType] || 0xf39c12;
    } else if (isNPC) {
      return 0xf39c12;
    } else if (data.shipColor) {
      return parseInt(data.shipColor.replace("#", ""), 16);
    }
    return 0x3498db;
  }

  /**
   * Get a deterministic portrait index based on ship ID
   * This ensures the same NPC always has the same portrait
   */
  private getPortraitIndex(shipId: string): number {
    const PORTRAIT_COUNT = 21; // Number of portrait images

    // Simple hash of the ship ID to get a consistent index
    let hash = 0;
    for (let i = 0; i < shipId.length; i++) {
      const char = shipId.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash) % PORTRAIT_COUNT;
  }

  private getPortraitLabel(data: ShipData, isNPC: boolean): string {
    if (!isNPC) return "SHIP";
    if (data.faction === "navy") return "N";
    if (data.faction === "pirate") return "P";
    if (data.faction === "bandit") return "B";
    if (data.faction === "merchant") return "M";
    return "NPC";
  }

  private getTypeLabel(
    data: ShipData,
    isLocal: boolean,
    isNPC: boolean,
  ): { text: string; color: string } {
    if (isLocal) return { text: "Your Ship", color: "#888888" };

    if (isNPC && data.faction) {
      const factionLabels: Record<string, { label: string; color: string }> = {
        navy: { label: "NPC", color: "#2980b9" },
        pirate: { label: "NPC", color: "#c0392b" },
        bandit: { label: "NPC", color: "#d35400" },
        merchant: { label: "NPC", color: "#2ecc71" },
      };
      const info = factionLabels[data.faction];
      if (info) return { text: info.label, color: info.color };
    }

    if (isNPC && data.behaviorType) {
      const behaviorLabels: Record<string, string> = {
        passive: "Passive",
        defensive: "Defensive",
        territorial: "Territorial",
        aggressive: "Aggressive",
        hunter: "Hunter",
      };
      return {
        text: behaviorLabels[data.behaviorType] || "NPC",
        color: "#888888",
      };
    }

    return { text: isNPC ? "NPC" : "Player", color: "#888888" };
  }

  private formatBounty(bounty: number): string {
    if (bounty >= 1000000000) return `${(bounty / 1000000000).toFixed(1)}B ₱`;
    if (bounty >= 1000000) return `${(bounty / 1000000).toFixed(1)}M ₱`;
    if (bounty >= 1000) return `${Math.floor(bounty / 1000)}K ₱`;
    return `${bounty} ₱`;
  }

  private shouldShowPlayerIdentityUI(): boolean {
    try {
      const saved: string | null = localStorage.getItem("showPlayerIdentityUI");
      return saved === null ? true : saved === "true";
    } catch {
      return true;
    }
  }

  private hexToNumber(color: string | undefined, fallback: number): number {
    if (!color) return fallback;
    const normalized: string = color.startsWith("#") ? color.slice(1) : color;
    const parsed: number = Number.parseInt(normalized, 16);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  private drawFlagPattern(
    gfx: Phaser.GameObjects.Graphics,
    patternId: string,
    x: number,
    y: number,
    width: number,
    height: number,
    primaryColor: number,
    secondaryColor: number,
  ): void {
    const pattern: string = patternId || "solid";

    if (pattern === "striped_h") {
      const stripeH: number = Math.max(1, Math.floor(height / 3));
      gfx.fillStyle(primaryColor, 0.95);
      gfx.fillRect(x, y, width, stripeH);
      gfx.fillStyle(secondaryColor, 0.95);
      gfx.fillRect(x, y + stripeH, width, stripeH);
      gfx.fillStyle(primaryColor, 0.95);
      gfx.fillRect(x, y + stripeH * 2, width, height - stripeH * 2);
      return;
    }

    if (pattern === "striped_v") {
      const stripeW: number = Math.max(1, Math.floor(width / 3));
      gfx.fillStyle(primaryColor, 0.95);
      gfx.fillRect(x, y, stripeW, height);
      gfx.fillStyle(secondaryColor, 0.95);
      gfx.fillRect(x + stripeW, y, stripeW, height);
      gfx.fillStyle(primaryColor, 0.95);
      gfx.fillRect(x + stripeW * 2, y, width - stripeW * 2, height);
      return;
    }

    if (pattern === "quartered") {
      const halfW: number = Math.floor(width / 2);
      const halfH: number = Math.floor(height / 2);
      gfx.fillStyle(primaryColor, 0.95);
      gfx.fillRect(x, y, halfW, halfH);
      gfx.fillStyle(secondaryColor, 0.95);
      gfx.fillRect(x + halfW, y, width - halfW, halfH);
      gfx.fillStyle(secondaryColor, 0.95);
      gfx.fillRect(x, y + halfH, halfW, height - halfH);
      gfx.fillStyle(primaryColor, 0.95);
      gfx.fillRect(x + halfW, y + halfH, width - halfW, height - halfH);
      return;
    }

    if (pattern === "diagonal") {
      gfx.fillStyle(primaryColor, 0.95);
      gfx.fillRect(x, y, width, height);

      gfx.fillStyle(secondaryColor, 0.95);
      for (let iy = 0; iy < height; iy++) {
        const cutoff: number = Math.floor(
          ((height - 1 - iy) / Math.max(1, height - 1)) * (width - 1),
        );
        for (let ix = cutoff; ix < width; ix++) {
          gfx.fillRect(x + ix, y + iy, 1, 1);
        }
      }
      return;
    }

    if (pattern === "cross") {
      gfx.fillStyle(primaryColor, 0.95);
      gfx.fillRect(x, y, width, height);

      const bandH: number = Math.max(2, Math.floor(height * 0.3));
      const bandW: number = Math.max(2, Math.floor(width * 0.25));
      const midY: number = y + Math.floor((height - bandH) / 2);
      const midX: number = x + Math.floor((width - bandW) / 2);
      gfx.fillStyle(secondaryColor, 0.95);
      gfx.fillRect(x, midY, width, bandH);
      gfx.fillRect(midX, y, bandW, height);
      return;
    }

    if (pattern === "saltire") {
      gfx.fillStyle(primaryColor, 0.95);
      gfx.fillRect(x, y, width, height);
      gfx.fillStyle(secondaryColor, 0.95);

      const thickness: number = 2;
      const steps: number = Math.max(width, height);
      for (let i = 0; i < steps; i++) {
        const t: number = steps <= 1 ? 0 : i / (steps - 1);
        const dx1: number = Math.round(t * (width - 1));
        const dy1: number = Math.round(t * (height - 1));
        gfx.fillRect(x + dx1, y + dy1, thickness, thickness);

        const dx2: number = Math.round(t * (width - 1));
        const dy2: number = Math.round((1 - t) * (height - 1));
        gfx.fillRect(x + dx2, y + dy2, thickness, thickness);
      }
      return;
    }

    if (pattern === "bordered") {
      gfx.fillStyle(secondaryColor, 0.95);
      gfx.fillRect(x, y, width, height);
      const pad: number = 2;
      gfx.fillStyle(primaryColor, 0.95);
      gfx.fillRect(
        x + pad,
        y + pad,
        Math.max(1, width - pad * 2),
        Math.max(1, height - pad * 2),
      );
      return;
    }

    gfx.fillStyle(primaryColor, 0.95);
    gfx.fillRect(x, y, width, height);
  }

  private drawFlagEmblem(
    gfx: Phaser.GameObjects.Graphics,
    emblemId: string,
    x: number,
    y: number,
    size: number,
    color: number,
  ): void {
    const id: string = emblemId || "none";
    if (id === "none") return;

    const patterns: Record<string, string[]> = {
      skull: ["011110", "111111", "110011", "111111", "011110", "001100"],
      anchor: ["001100", "001100", "111111", "001100", "011110", "010010"],
      star: ["001000", "001000", "111111", "001000", "001000", "000000"],
      compass: ["001100", "011110", "111111", "011110", "001100", "000000"],
      ship: ["001000", "011100", "111111", "011110", "001100", "000000"],
      wave: ["000000", "010010", "101101", "010010", "000000", "000000"],
      diamond: ["001000", "011100", "111110", "011100", "001000", "000000"],
      shield: ["011110", "111111", "111111", "011110", "001100", "000000"],
      lightning: ["001100", "011000", "110000", "011000", "001100", "000000"],
      crown: ["010010", "101101", "111111", "111111", "011110", "000000"],
      sword: ["001000", "001000", "001000", "001000", "111111", "001000"],
      coin: ["011110", "110011", "101101", "110011", "011110", "000000"],
      trident: ["101101", "011110", "001000", "001000", "001000", "111111"],
      dragon: ["111100", "100010", "100010", "100010", "111100", "000000"],
      phoenix: ["111110", "100000", "111100", "100000", "111110", "000000"],
      kraken: ["100010", "100100", "111000", "100100", "100010", "000000"],
      moon: ["011110", "110011", "110000", "110011", "011110", "000000"],
      sun: ["101101", "011110", "111111", "011110", "101101", "000000"],
      heart: ["110011", "111111", "111111", "011110", "001100", "000000"],
      crossbones: ["100001", "010010", "001100", "001100", "010010", "100001"],
      flag: ["110000", "111100", "110100", "110000", "110000", "110000"],
    };

    const pattern: string[] | undefined = patterns[id];
    const px: number = 1;
    const rows: string[] = Array.isArray(pattern)
      ? pattern
      : ["011110", "100001", "101101", "100001", "011110", "000000"];

    gfx.fillStyle(color, 0.9);
    for (let row = 0; row < rows.length; row++) {
      const line: string = rows[row];
      for (let col = 0; col < line.length; col++) {
        if (line[col] !== "1") continue;
        gfx.fillRect(x + col * px, y + row * px, px, px);
      }
    }
  }

  destroy(): void {
    this.hideShipTooltip();
    this.ships.forEach((ship) => ship.destroy());
    this.ships.clear();
    this.localShip = null;
  }

  private applyVisibilityLimits(): void {
    if (this.visibilityLimitTiles === null) {
      // No limit: ensure all ships visible
      this.ships.forEach((ship) => ship.setVisible(true));
      return;
    }

    const local = this.localShip;
    if (!local) return;

    this.ships.forEach((ship) => {
      // Always show local ship
      if (ship === local) {
        ship.setVisible(true);
        return;
      }
      const distTiles =
        Phaser.Math.Distance.Between(local.x, local.y, ship.x, ship.y) /
        TILE_SIZE;
      ship.setVisible(
        this.visibilityLimitTiles !== null
          ? distTiles <= this.visibilityLimitTiles
          : true,
      );
    });
  }
}
